import { ErrorBoundary } from 'react-error-boundary';
import { useController } from 'react-hook-form';

import { MultiSelect, MultiSelectProps } from '@mantine/core';

import { useDatasheetReadOnly } from 'components/datasheet/DatasheetMantineProvider';
import { DatasheetTableValue } from 'components/datasheet/DatasheetTableValue';

import { useReadOnlyPlaceholder } from './hooks/use-readonly-placeholder';

export type MultiSelectFieldProps = MultiSelectProps & {
    name: string;
};

const MultiSelectField = ({ name, defaultValue, ...props }: MultiSelectFieldProps) => {
    const {
        field,
        fieldState: { isDirty, isTouched, error },
    } = useController({ name, defaultValue });

    const readOnly = useDatasheetReadOnly();
    const ref = useReadOnlyPlaceholder('Not specified', field.value?.length === 0);

    const value: string[] | undefined = field.value ? Object.values(field.value) : field.value;

    if (readOnly) {
        if (!value?.length) {
            return null;
        }

        return (
            <DatasheetTableValue>
                {value.map((item) => getOptionLabel(props.data, item)).join(', ')}
            </DatasheetTableValue>
        );
    }

    return (
        <ErrorBoundary fallback={null} onError={(error) => console.error(name, field.value, error)}>
            <MultiSelect
                placeholder="Select one or many options"
                {...field}
                value={value}
                {...props}
                error={isTouched && error?.message}
                ref={ref}
                wrapperProps={{ 'data-dirty': isDirty, 'data-touched': isTouched }}
            />
        </ErrorBoundary>
    );
};

type OptionLike = string | { value?: string; label?: string; items?: readonly OptionLike[] };

const getOptionLabel = (data: readonly OptionLike[] | undefined, value: string): string => {
    for (const item of data ?? []) {
        if (typeof item === 'string') {
            if (item === value) return item;
        } else if (item.items) {
            const label = getOptionLabel(item.items, value);

            if (label !== value) return label;
        } else if (item.value === value) {
            return item.label ?? value;
        }
    }

    return value;
};

export { MultiSelectField };
